import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { getJwtToken, getUserPermissions } from "../../api/services/hroneApi";
import { setUserLoggedOut } from "./notificationSlice";

export const fetchPermissions = createAsyncThunk(
  "permissions/fetchPermissions",
  async () => {
    if (!getJwtToken()) {
      return [];
    }
    const perms = await getUserPermissions();
    return Array.isArray(perms) ? perms : [];
  },
);

const permissionsSlice = createSlice({
  name: "permissions",
  initialState: {
    permissions: [],
    loading: true,
  },
  reducers: {
    clearPermissions: (state) => {
      state.permissions = [];
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPermissions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload;
        state.loading = false;
      })
      .addCase(fetchPermissions.rejected, (state) => {
        state.permissions = [];
        state.loading = false;
      })
      .addCase(setUserLoggedOut, (state) => {
        state.permissions = [];
        state.loading = false;
      });
  },
});

export const { clearPermissions } = permissionsSlice.actions;

export const selectPermissions = (state) => state.permissions.permissions;
export const selectPermissionsLoading = (state) => state.permissions.loading;

/**
 * Hook for reading permissions from Redux and checking access.
 * Import from this slice the same way other slices export actions —
 * e.g. mobileSlice exports collapseLeftnavBar, this exports usePermissions.
 */
export const usePermissions = () => {
  const dispatch = useDispatch();
  const permissions = useSelector(selectPermissions);
  const loading = useSelector(selectPermissionsLoading);

  const refreshPermissions = () => dispatch(fetchPermissions());

  const hasPermission = (permissionCode) => {
    if (!permissionCode) return false;
    return permissions.includes(permissionCode);
  };

  const hasAnyPermission = (permissionCodes = []) => {
    if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) {
      return false;
    }
    return permissionCodes.some((code) => permissions.includes(code));
  };

  const hasAllPermissions = (permissionCodes = []) => {
    if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) {
      return false;
    }
    return permissionCodes.every((code) => permissions.includes(code));
  };

  return {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions,
  };
};

export default permissionsSlice.reducer;
